import { App, Tabs } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMe } from "@/modules/auth/hooks/useAuth";
import { useOrganization } from "@/modules/organizations/hooks/useOrganizations";
import { PageHeader } from "@/shared/components/PageHeader";
import { useUrlParam, useUrlParams } from "@/shared/hooks/useUrlState";
import { AttendanceFilter } from "../components/AttendanceFilter";
import { AttendancePhotoGallery } from "../components/AttendancePhotoGallery";
import { AttendanceTable } from "../components/AttendanceTable";
import { dailyAttendanceService } from "../services/daily-attendance.service";
import { DEFAULT_DATE_RANGE } from "../utils/attendance.constants";

const ORG_SELECT_DEBOUNCE_MS = 200;

export function DailyAttendancePage() {
	const { message } = App.useApp();
	const { data: meUser } = useMe();

	// Dates, applied org and active tab live in the URL so back/forward restores them.
	const { searchParams, setParams } = useUrlParams();
	const [activeTab, setActiveTab] = useUrlParam("tab", "table");
	const startDate = searchParams.get("start") ?? DEFAULT_DATE_RANGE.start;
	const endDate = searchParams.get("end") ?? DEFAULT_DATE_RANGE.end;
	const organizationId = searchParams.get("org");

	// Org name is derived from the applied org id so it survives page reloads.
	const { data: selectedOrg } = useOrganization(organizationId ?? undefined);
	const organizationName = selectedOrg?.name ?? "";

	// Pending selection shows in the tree select immediately; the URL (and the
	// queries keyed off it) updates only after the debounce settles.
	const [pendingOrgId, setPendingOrgId] = useState<string | null | undefined>(undefined);
	const [isExporting, setIsExporting] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const autoSelectedRef = useRef(false);
	// RangePicker reports start and end via separate callbacks in one change
	// event; buffer the start so both dates land in a single history entry.
	const pendingStartRef = useRef<string | null>(null);

	// Auto-select the first organization when meUser loads and no org is in the URL
	useEffect(() => {
		if (autoSelectedRef.current) {
			return;
		}
		if (organizationId) {
			autoSelectedRef.current = true;
			return;
		}
		const firstOrg = meUser?.organizations?.[0];
		if (firstOrg) {
			autoSelectedRef.current = true;
			setParams({ org: firstOrg.id }, { replace: true });
		}
	}, [meUser, organizationId, setParams]);

	const handleOrganizationChange = useCallback(
		(value: string | null) => {
			setPendingOrgId(value);
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
			debounceRef.current = setTimeout(() => {
				debounceRef.current = null;
				setPendingOrgId(undefined);
				setParams({ org: value });
			}, ORG_SELECT_DEBOUNCE_MS);
		},
		[setParams]
	);

	const handleStartDateChange = (d: string | null) => {
		if (d != null) {
			pendingStartRef.current = d;
		}
	};

	const handleEndDateChange = (d: string | null) => {
		if (d == null) {
			return;
		}
		const start = pendingStartRef.current ?? startDate;
		pendingStartRef.current = null;
		// Both dates in one patch = one history entry; defaults stay out of the URL.
		setParams({
			start: start === DEFAULT_DATE_RANGE.start ? null : start,
			end: d === DEFAULT_DATE_RANGE.end ? null : d,
		});
	};

	const handleReset = () => {
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
			debounceRef.current = null;
		}
		setPendingOrgId(undefined);
		pendingStartRef.current = null;
		// Clear every URL-backed value in a single history entry.
		setParams({ org: null, start: null, end: null, tab: null });
	};

	const handleExport = async () => {
		if (!(organizationId && startDate && endDate)) {
			return;
		}
		setIsExporting(true);
		try {
			const blob = await dailyAttendanceService.exportAttendanceReport({
				organizationId,
				startDate,
				endDate,
				format: "xlsx",
			});
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `davomat-${organizationName}-${startDate}-${endDate}.xlsx`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		} catch {
			message.error("Eksport muvaffaqiyatsiz tugadi");
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<div>
			<PageHeader title="Davomat hisoboti" />

			<AttendanceFilter
				startDate={startDate}
				endDate={endDate}
				organizationId={pendingOrgId !== undefined ? pendingOrgId : organizationId}
				onStartDateChange={handleStartDateChange}
				onEndDateChange={handleEndDateChange}
				onOrganizationChange={handleOrganizationChange}
				onReset={handleReset}
				onExport={handleExport}
				isExporting={isExporting}
			/>

			{organizationId && organizationName && (
				<Tabs
					activeKey={activeTab}
					onChange={setActiveTab}
					items={[
						{
							key: "table",
							label: "Jadval",
							children: (
								<AttendanceTable
									organizationId={organizationId}
									organizationName={organizationName}
									startDate={startDate}
									endDate={endDate}
								/>
							),
						},
						{
							key: "gallery",
							label: "Foto galereya",
							children: (
								<AttendancePhotoGallery
									organizationId={organizationId}
									startDate={startDate}
									endDate={endDate}
								/>
							),
						},
					]}
				/>
			)}
		</div>
	);
}
